<script setup>
import { computed, onMounted, onUnmounted, watch, ref } from 'vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation } from 'swiper/modules'

import 'swiper/css'
import 'swiper/css/navigation'

const props = defineProps({
    show: {
        type: Boolean,
        default: false,
    },

    storageType: {
        type: String,
        default: 'businesses',
    },

    maxWidth: {
        type: String,
        default: '2xl',
    },

    closeable: {
        type: Boolean,
        default: true,
    },

    images: {
        type: Array,
        required: false,
        default: () => [],
    },

    current_image_id: {
        type: Number,
        default: 0,
    },
})

const emit = defineEmits(['close'])

const modalKey = ref(Date.now())

watch(
    () => props.show,
    (newVal) => {
        if (newVal) {
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = null
            modalKey.value = Date.now() // Update key to force re-render
        }
    }
)

const close = () => {
    if (props.closeable) {
        emit('close')
    }
}

const closeOnEscape = (e) => {
    if (e.key === 'Escape' && props.show) {
        close()
    }
}

onMounted(() => document.addEventListener('keydown', closeOnEscape))

onUnmounted(() => {
    document.removeEventListener('keydown', closeOnEscape)
    document.body.style.overflow = null
})

const maxWidthClass = computed(() => {
    return {
        sm: 'sm:max-w-sm',
        md: 'sm:max-w-md',
        lg: 'sm:max-w-lg',
        xl: 'sm:max-w-xl',
        '2xl': 'sm:max-w-2xl',
        '3xl': 'sm:max-w-3xl',
        '4xl': 'sm:max-w-4xl',
        '7xl': 'sm:max-w-7xl',
    }[props.maxWidth]
})

const orderedImages = computed(() => {
    const imagesCopy = [...props.images]
    const idx = imagesCopy.findIndex((image) => image.id === props.current_image_id)
    if (idx !== -1) {
        const [matchedImage] = imagesCopy.splice(idx, 1)
        imagesCopy.unshift(matchedImage)
    }
    return imagesCopy
})

const shouldLoop = computed(() => orderedImages.value.length > 1)
</script>

<template>
    <teleport to="body">
        <transition leave-active-class="duration-200">
            <div
                v-if="show"
                :key="modalKey"
                class="fixed inset-0 z-50 overflow-y-auto px-4 py-6 sm:px-0"
                scroll-region
            >
                <transition
                    enter-active-class="ease-out duration-300"
                    enter-from-class="opacity-0"
                    enter-to-class="opacity-100"
                    leave-active-class="ease-in duration-200"
                    leave-from-class="opacity-100"
                    leave-to-class="opacity-0"
                >
                    <div
                        v-show="show"
                        class="fixed inset-0 transform transition-all"
                        @click="close"
                    >
                        <div class="absolute inset-0 bg-gray-500 opacity-75" />
                    </div>
                </transition>

                <transition
                    enter-active-class="ease-out duration-300"
                    enter-from-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enter-to-class="opacity-100 translate-y-0 sm:scale-100"
                    leave-active-class="ease-in duration-200"
                    leave-from-class="opacity-100 translate-y-0 sm:scale-100"
                    leave-to-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                    <div
                        v-show="show"
                        class="relative mx-auto grid h-full w-full transform overflow-hidden rounded-lg bg-white/40 shadow-xl backdrop-blur-xl transition-all"
                        :class="maxWidthClass"
                    >
                        <slot name="content" />

                        <Swiper
                            v-if="orderedImages.length"
                            :loop="shouldLoop"
                            :modules="[Navigation]"
                            slidesPerView="1"
                            :navigation="true"
                            class="h-full w-full"
                        >
                            <SwiperSlide
                                v-for="image in orderedImages"
                                :key="image.id"
                                class="m-auto flex h-[40vh] items-center justify-center sm:h-full"
                            >
                                <img
                                    class="h-full w-full object-contain"
                                    :src="'/storage/' + storageType + '/' + image.imageable_id + '/images/' + image.image"
                                    loading="lazy"
                                    :alt="image.image"
                                />
                            </SwiperSlide>
                        </Swiper>
                    </div>
                </transition>
            </div>
        </transition>
    </teleport>
</template>
